import { ChevronDown, Target } from 'lucide-react'
import React from 'react'
import { getStrikeColorClass, getStrikeLabel } from '../../lib/table-utils'
import type { AnalysisResult, MarketData } from '../../types'
import { MaterialCard } from '../ui/MaterialCard'
import { StrikeSelectionPanel } from './StrikeSelectionPanel'
import type { InspectionMode } from './types'

interface TargetSelectionProps {
    mode: InspectionMode
    availableStrikes: number[]
    selectedSingleStrike: number
    setSelectedSingleStrike: (s: number) => void
    selectedCeStrike: number
    setSelectedCeStrike: (s: number) => void
    selectedPeStrike: number
    setSelectedPeStrike: (s: number) => void
    selectedCustomCeStrikes: number[]
    setSelectedCustomCeStrikes: React.Dispatch<React.SetStateAction<number[]>>
    selectedCustomPeStrikes: number[]
    setSelectedCustomPeStrikes: React.Dispatch<React.SetStateAction<number[]>>
    analysis: AnalysisResult | null
    activeMarketData: MarketData | null
}

export const TargetSelection: React.FC<TargetSelectionProps> = React.memo(
    ({
        mode,
        availableStrikes,
        selectedSingleStrike,
        setSelectedSingleStrike,
        selectedCeStrike,
        setSelectedCeStrike,
        selectedPeStrike,
        setSelectedPeStrike,
        selectedCustomCeStrikes,
        setSelectedCustomCeStrikes,
        selectedCustomPeStrikes,
        setSelectedCustomPeStrikes,
        analysis,
        activeMarketData,
    }) => {
        const getLabelForStrike = (strike: number) => {
            return getStrikeLabel(strike, analysis)
        }

        // Fix React `<select>` async options bug: Ensure the selected strike is always in the options list.
        const guaranteedSingleStrikes = React.useMemo(() => {
            if (selectedSingleStrike > 0 && !availableStrikes.includes(selectedSingleStrike)) {
                return [...availableStrikes, selectedSingleStrike].sort((a, b) => a - b)
            }
            return availableStrikes
        }, [availableStrikes, selectedSingleStrike])

        const guaranteedCeStrikes = React.useMemo(() => {
            if (selectedCeStrike > 0 && !availableStrikes.includes(selectedCeStrike)) {
                return [...availableStrikes, selectedCeStrike].sort((a, b) => a - b)
            }
            return availableStrikes
        }, [availableStrikes, selectedCeStrike])

        const guaranteedPeStrikes = React.useMemo(() => {
            if (selectedPeStrike > 0 && !availableStrikes.includes(selectedPeStrike)) {
                return [...availableStrikes, selectedPeStrike].sort((a, b) => a - b)
            }
            return availableStrikes
        }, [availableStrikes, selectedPeStrike])

        // Helpers for Custom Mode
        const toggleCustomStrike = (strike: number, type: 'CE' | 'PE') => {
            if (type === 'CE') {
                setSelectedCustomCeStrikes((prev) =>
                    prev.includes(strike) ? prev.filter((s) => s !== strike) : [...prev, strike].sort((a, b) => a - b),
                )
            } else {
                setSelectedCustomPeStrikes((prev) =>
                    prev.includes(strike) ? prev.filter((s) => s !== strike) : [...prev, strike].sort((a, b) => a - b),
                )
            }
        }

        const selectAll = (type: 'CE' | 'PE') => {
            if (type === 'CE') setSelectedCustomCeStrikes([...availableStrikes])
            else setSelectedCustomPeStrikes([...availableStrikes])
        }

        const clearAll = (type: 'CE' | 'PE') => {
            if (type === 'CE') setSelectedCustomCeStrikes([])
            else setSelectedCustomPeStrikes([])
        }

        const handleRangeSelect = (type: 'CE' | 'PE', range: 'ATM' | 'ITM' | 'OTM') => {
            if (!activeMarketData) return
            const spot = activeMarketData.spotPrice || 0
            const strikes = activeMarketData.options.map((o: any) => o.strike).sort((a: number, b: number) => a - b)

            let toSelect: number[] = []

            if (range === 'ATM') {
                // Select 5 closest to spot
                const sortedByDist = [...strikes].sort(
                    (a: number, b: number) => Math.abs(a - spot) - Math.abs(b - spot),
                )
                toSelect = sortedByDist.slice(0, 5).sort((a: number, b: number) => a - b)
            } else {
                if (type === 'CE') {
                    if (range === 'ITM') toSelect = strikes.filter((s: number) => s < spot)
                    if (range === 'OTM') toSelect = strikes.filter((s: number) => s > spot)
                } else {
                    if (range === 'ITM') toSelect = strikes.filter((s: number) => s > spot)
                    if (range === 'OTM') toSelect = strikes.filter((s: number) => s < spot)
                }
                // Limit to 10
                if (toSelect.length > 10) {
                    if (range === 'ITM' && type === 'CE') toSelect = toSelect.slice(-10)
                    if (range === 'OTM' && type === 'CE') toSelect = toSelect.slice(0, 10)
                    if (range === 'ITM' && type === 'PE') toSelect = toSelect.slice(0, 10)
                    if (range === 'OTM' && type === 'PE') toSelect = toSelect.slice(-10)
                }
            }

            if (type === 'CE') setSelectedCustomCeStrikes(toSelect)
            else setSelectedCustomPeStrikes(toSelect)
        }

        return (
            <MaterialCard className="border-none shadow-xl bg-white dark:bg-slate-900 relative group p-0 transition-colors duration-300">
                <div className="p-1">
                    {mode === 'Single' && (
                        <div className="max-w-md mx-auto py-2 animate-in fade-in zoom-in-95 duration-500">
                            <label className="text-[10px] font-black text-indigo-300 dark:text-indigo-400/60 uppercase tracking-widest mb-3 text-center flex items-center justify-center gap-2">
                                <Target size={14} /> Target Strike Inspection
                            </label>
                            <div className="relative group/select w-full max-w-[280px] mx-auto">
                                <div className="absolute inset-0 bg-indigo-500/5 dark:bg-indigo-500/10 rounded-2xl transform rotate-1 group-hover/select:rotate-2 transition-transform duration-300"></div>
                                <select
                                    value={selectedSingleStrike}
                                    onChange={(e) => setSelectedSingleStrike(Number(e.target.value))}
                                    className="relative w-full appearance-none bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 group-hover/select:border-indigo-300 dark:group-hover/select:border-indigo-500/50 focus:border-indigo-500 dark:focus:border-indigo-400 rounded-2xl px-4 py-3 text-center text-3xl premium-cell-mono font-black text-slate-800 dark:text-slate-100 outline-none transition-all cursor-pointer shadow-sm group-hover/select:shadow-lg group-hover/select:shadow-indigo-500/10 z-10"
                                >
                                    {guaranteedSingleStrikes.map((s, i) => (
                                        <option
                                            key={`${s}-${i}`}
                                            value={s}
                                            className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                                        >
                                            {s}
                                        </option>
                                    ))}
                                </select>
                                <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none z-20">
                                    <span className="bg-indigo-50 dark:bg-indigo-900/30 text-indigo-400 dark:text-indigo-300 p-1.5 rounded-lg opacity-50 group-hover/select:opacity-100 transition-opacity">
                                        <ChevronDown size={18} strokeWidth={3} />
                                    </span>
                                </div>
                            </div>
                            <div className="text-center mt-4">
                                <span
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide border shadow-sm premium-cell-mono ${getStrikeColorClass(getLabelForStrike(selectedSingleStrike)?.text || null)}`}
                                >
                                    {getLabelForStrike(selectedSingleStrike)?.text ? (
                                        <>
                                            <Target size={12} /> {getLabelForStrike(selectedSingleStrike)?.text} Level
                                        </>
                                    ) : (
                                        'Neutral Strike'
                                    )}
                                </span>
                            </div>
                        </div>
                    )}

                    {mode === 'Multiple' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                            {/* CE Selection */}
                            <div className="space-y-3">
                                <label className="text-[10px] font-black text-rose-500 dark:text-rose-400 uppercase tracking-widest text-center flex items-center justify-center gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse"></div>
                                    Call (CE) Leg
                                </label>
                                <div className="relative group/select">
                                    <select
                                        value={selectedCeStrike}
                                        onChange={(e) => setSelectedCeStrike(Number(e.target.value))}
                                        className="w-full appearance-none bg-white dark:bg-slate-800 border-2 border-rose-50 dark:border-rose-900/30 group-hover/select:border-rose-200 dark:group-hover/select:border-rose-700/50 focus:border-rose-500 dark:focus:border-rose-500 rounded-2xl px-4 py-4 text-center text-2xl premium-cell-mono font-black text-rose-900 dark:text-rose-300 outline-none cursor-pointer shadow-sm transition-all"
                                    >
                                        {guaranteedCeStrikes.map((s, i) => (
                                            <option
                                                key={`ce-${s}-${i}`}
                                                value={s}
                                                className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                                            >
                                                {s}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown
                                        size={20}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-rose-300 dark:text-rose-500 pointer-events-none"
                                    />
                                </div>
                            </div>

                            {/* PE Selection */}
                            <div className="space-y-3">
                                <label className="text-[10px] font-black text-emerald-500 dark:text-emerald-400 uppercase tracking-widest text-center flex items-center justify-center gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
                                    Put (PE) Leg
                                </label>
                                <div className="relative group/select">
                                    <select
                                        value={selectedPeStrike}
                                        onChange={(e) => setSelectedPeStrike(Number(e.target.value))}
                                        className="w-full appearance-none bg-white dark:bg-slate-800 border-2 border-emerald-50 dark:border-emerald-900/30 group-hover/select:border-emerald-200 dark:group-hover/select:border-emerald-700/50 focus:border-emerald-500 dark:focus:border-emerald-500 rounded-2xl px-4 py-4 text-center text-2xl premium-cell-mono font-black text-emerald-900 dark:text-emerald-300 outline-none cursor-pointer shadow-sm transition-all"
                                    >
                                        {guaranteedPeStrikes.map((s, i) => (
                                            <option
                                                key={`pe-${s}-${i}`}
                                                value={s}
                                                className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                                            >
                                                {s}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown
                                        size={20}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-emerald-300 dark:text-emerald-500 pointer-events-none"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {mode === 'Custom' && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
                            <StrikeSelectionPanel
                                title="Call Strikes (Resistances)"
                                strikes={availableStrikes}
                                selectedStrikes={selectedCustomCeStrikes}
                                onToggle={(s) => toggleCustomStrike(s, 'CE')}
                                onSelectAll={() => selectAll('CE')}
                                onClear={() => clearAll('CE')}
                                onSelectRange={(r) => handleRangeSelect('CE', r)}
                                colorTheme="rose"
                            />
                            <StrikeSelectionPanel
                                title="Put Strikes (Supports)"
                                strikes={availableStrikes}
                                selectedStrikes={selectedCustomPeStrikes}
                                onToggle={(s) => toggleCustomStrike(s, 'PE')}
                                onSelectAll={() => selectAll('PE')}
                                onClear={() => clearAll('PE')}
                                onSelectRange={(r) => handleRangeSelect('PE', r)}
                                colorTheme="emerald"
                            />
                        </div>
                    )}
                </div>
            </MaterialCard>
        )
    },
)
